import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { uploadFile } from "@/api/uploadFile";
import { createTransientId } from "@/shared/lib/transientId";
import { uploadSizeViolation } from "@/shared/lib/uploadLimits";
import { useToast } from "@/shared/ui";
import { type ComposerAttachment, isImageMime, MAX_ATTACHMENTS } from "../lib/attachments";

interface ComposerAttachmentsApi {
  attachments: ComposerAttachment[];
  /** Queue files for upload (capped at MAX_ATTACHMENTS total). */
  addFiles: (files: File[]) => void;
  /** Remove one attachment, revoking its preview URL. */
  remove: (localId: string) => void;
  /** True while any attachment is still uploading — send is blocked until done. */
  isUploading: boolean;
  /**
   * Empty the list and return the ready attachments for sending. The returned
   * attachments' object URLs are handed to the caller (NOT revoked here) so the
   * optimistic user bubble can keep showing the preview; the caller owns revoke.
   * Object URLs of dropped (errored/uploading) attachments are revoked.
   */
  take: () => ComposerAttachment[];
}

/**
 * Owns the composer's pending attachments: the 3-step direct upload per file
 * (via the shared `uploadFile`), per-item status, and object-URL lifecycle for
 * image previews. The chat composer reads `attachments`/`isUploading`; on send,
 * `take()` hands the ready ones to the run.
 */
export function useComposerAttachments(): ComposerAttachmentsApi {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);

  // Mirror for synchronous reads (room calc, take) without stale closures.
  const ref = useRef<ComposerAttachment[]>([]);
  ref.current = attachments;

  // Revoke any still-tracked preview URLs on unmount.
  useEffect(
    () => () => {
      for (const a of ref.current) if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
    },
    [],
  );

  const addFiles = useCallback((files: File[]) => {
    // Reject files over the per-file size cap up front (the backend enforces
    // this too, but this avoids a wasted upload and gives instant feedback).
    const sized = files.filter((file) => {
      const violation = uploadSizeViolation(file);
      if (violation) {
        toast(t("chat.attachments.tooLarge", { max: Math.round(violation.maxBytes / (1024 * 1024)) }));
      }
      return !violation;
    });

    const room = MAX_ATTACHMENTS - ref.current.length;
    const accepted = sized.slice(0, Math.max(0, room));
    // Soft cap: warn when files are dropped because the message is full.
    if (accepted.length < sized.length) {
      toast(t("chat.attachments.maxFiles", { max: MAX_ATTACHMENTS }));
    }
    if (accepted.length === 0) return;

    const queued = accepted.map((file) => {
      const localId = createTransientId("attachment");
      const previewUrl = isImageMime(file.type) ? URL.createObjectURL(file) : undefined;
      const entry: ComposerAttachment = {
        localId,
        status: "uploading",
        fileName: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        previewUrl,
      };
      return { entry, file, localId };
    });

    setAttachments((prev) => [...prev, ...queued.map((q) => q.entry)]);

    // Launch uploads outside the state updater (StrictMode-safe: updaters can run twice).
    for (const { file, localId } of queued) {
      void uploadFile(file)
        .then((uploaded) =>
          setAttachments((cur) =>
            cur.map((a) =>
              a.localId === localId ? { ...a, status: "ready", fileId: uploaded.id } : a,
            ),
          ),
        )
        .catch(() =>
          setAttachments((cur) =>
            cur.map((a) => (a.localId === localId ? { ...a, status: "error" } : a)),
          ),
        );
    }
  }, [toast, t]);

  const remove = useCallback((localId: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.localId === localId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((a) => a.localId !== localId);
    });
  }, []);

  const take = useCallback((): ComposerAttachment[] => {
    const ready = ref.current.filter((a) => a.status === "ready" && a.fileId);
    const keep = new Set(ready.map((a) => a.localId));
    // Revoke previews we are NOT handing off (errored / still-uploading).
    for (const a of ref.current) {
      if (!keep.has(a.localId) && a.previewUrl) URL.revokeObjectURL(a.previewUrl);
    }
    setAttachments([]);
    return ready;
  }, []);

  return {
    attachments,
    addFiles,
    remove,
    isUploading: attachments.some((a) => a.status === "uploading"),
    take,
  };
}
